import React, { useEffect, useState } from "react";
import { api, formatErr, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ScrollText, Search, Filter, User, Calendar, RefreshCw, FileType, Download } from "lucide-react";

const ACTION_LABELS = {
  "invoice.create": { l: "إنشاء فاتورة", c: "bg-emerald-50 text-emerald-700" },
  "invoice.update": { l: "تعديل فاتورة", c: "bg-amber-50 text-amber-700" },
  "invoice.delete": { l: "حذف فاتورة", c: "bg-destructive/10 text-destructive" },
  "contract.create": { l: "إنشاء عقد", c: "bg-emerald-50 text-emerald-700" },
  "contract.update": { l: "تعديل عقد", c: "bg-amber-50 text-amber-700" },
  "contract.delete": { l: "حذف عقد", c: "bg-destructive/10 text-destructive" },
  "product.price_change": { l: "تغيير سعر", c: "bg-primary/10 text-primary" },
  "product.update": { l: "تعديل صنف", c: "bg-slate-50 text-slate-700" },
  "product.delete": { l: "حذف صنف", c: "bg-destructive/10 text-destructive" },
  "custom_role.create": { l: "إنشاء دور مخصص", c: "bg-emerald-50 text-emerald-700" },
  "custom_role.update": { l: "تعديل دور مخصص", c: "bg-amber-50 text-amber-700" },
  "custom_role.delete": { l: "حذف دور مخصص", c: "bg-destructive/10 text-destructive" },
  "backup.trigger": { l: "إنشاء نسخة احتياطية", c: "bg-primary/10 text-primary" },
  "backup.download": { l: "تنزيل نسخة", c: "bg-slate-50 text-slate-700" },
  "backup.delete": { l: "حذف نسخة", c: "bg-destructive/10 text-destructive" },
};

export default function AuditLog() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ from_date: "", to_date: "", action: "", resource_type: "", actor_id: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/audit-logs", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const clearFilters = () => {
    setF({ from_date: "", to_date: "", action: "", resource_type: "", actor_id: "" });
    setTimeout(load, 50);
  };

  const doExport = async (fmt) => {
    try {
      const token = localStorage.getItem("access_token");
      const params = new URLSearchParams({ format: fmt });
      Object.entries(f).forEach(([k, v]) => { if (v) params.set(k, v); });
      const r = await fetch(`${API}/audit-logs/export?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("فشل التصدير");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `audit_log.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      toast.success("تم بدء التنزيل");
    } catch (e) { toast.error(e.message || "فشل التصدير"); }
  };

  const actionInfo = (a) => ACTION_LABELS[a] || { l: a, c: "bg-slate-50 text-slate-700" };

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold flex items-center gap-2"><ScrollText className="w-7 h-7 text-primary" /> سجل التدقيق</h1>
          <p className="text-sm text-muted-foreground mt-1">من أنشأ أو عدّل أو حذف ماذا ومتى · فواتير · عقود · أسعار · أدوار · نسخ احتياطية</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={()=>doExport("pdf")} className="gap-2" data-testid="audit-export-pdf"><FileType className="w-4 h-4" /> PDF</Button>
          <Button variant="outline" onClick={()=>doExport("excel")} className="gap-2" data-testid="audit-export-excel"><Download className="w-4 h-4" /> Excel</Button>
        </div>
      </div>

      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3 text-sm font-bold"><Filter className="w-4 h-4" /> تصفية</div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div><Label className="text-xs">من تاريخ</Label><Input type="date" value={f.from_date} onChange={(e)=>setF({...f, from_date:e.target.value})} className="h-9" data-testid="audit-from" /></div>
          <div><Label className="text-xs">إلى تاريخ</Label><Input type="date" value={f.to_date} onChange={(e)=>setF({...f, to_date:e.target.value})} className="h-9" data-testid="audit-to" /></div>
          <div>
            <Label className="text-xs">نوع الحركة</Label>
            <select value={f.action} onChange={(e)=>setF({...f, action:e.target.value})} className="h-9 w-full px-2 border border-border rounded-md bg-background text-sm" data-testid="audit-action">
              <option value="">الكل</option>
              {Object.entries(ACTION_LABELS).map(([k,v]) => <option key={k} value={k}>{v.l}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">نوع المورد</Label>
            <select value={f.resource_type} onChange={(e)=>setF({...f, resource_type:e.target.value})} className="h-9 w-full px-2 border border-border rounded-md bg-background text-sm" data-testid="audit-resource">
              <option value="">الكل</option>
              <option value="invoice">فاتورة</option>
              <option value="contract">عقد</option>
              <option value="product">صنف</option>
              <option value="custom_role">دور مخصص</option>
              <option value="backup">نسخة احتياطية</option>
            </select>
          </div>
          <div className="flex items-end gap-2 md:col-span-2">
            <Button onClick={load} className="h-9 gap-2 flex-1" data-testid="audit-apply"><Search className="w-4 h-4" /> بحث</Button>
            <Button variant="outline" onClick={clearFilters} className="h-9" data-testid="audit-clear">مسح</Button>
            <Button variant="outline" onClick={load} disabled={loading} className="h-9" title="تحديث"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></Button>
          </div>
        </div>
        <div className="mt-3 text-xs text-muted-foreground">
          إجمالي السجلات: <span className="font-bold tabular-nums">{total}</span> · معروض: <span className="font-bold tabular-nums">{items.length}</span>
        </div>
      </Card>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>التاريخ</TableHead>
              <TableHead>المستخدم</TableHead>
              <TableHead>الدور</TableHead>
              <TableHead>الحركة</TableHead>
              <TableHead>المورد</TableHead>
              <TableHead>التفاصيل</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">لا توجد سجلات مطابقة</TableCell></TableRow>}
            {items.map(a => {
              const info = actionInfo(a.action);
              return (
                <TableRow key={a.id} data-testid={`audit-row-${a.id}`}>
                  <TableCell className="text-xs whitespace-nowrap">{new Date(a.created_at).toLocaleString("ar-EG")}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User className="w-3 h-3 text-muted-foreground shrink-0" />
                      <div className="min-w-0">
                        <div className="font-semibold text-sm truncate">{a.actor_name || a.actor_email}</div>
                        <div className="text-[10px] text-muted-foreground truncate">{a.actor_email}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-xs">{a.actor_role}</TableCell>
                  <TableCell><span className={`text-xs px-2 py-0.5 rounded font-semibold ${info.c}`}>{info.l}</span></TableCell>
                  <TableCell className="text-xs"><div>{a.resource_type}</div><div className="font-mono text-[10px] text-muted-foreground truncate max-w-[120px]" title={a.resource_id}>{a.resource_id ? a.resource_id.substring(0,8)+"..." : "—"}</div></TableCell>
                  <TableCell className="text-xs max-w-md">
                    {a.changes && Object.keys(a.changes).length > 0 ? (
                      <div className="space-y-0.5 font-mono text-[10px]">
                        {Object.entries(a.changes).slice(0, 4).map(([k,v]) => (
                          <div key={k}><span className="text-muted-foreground">{k}:</span> <span>{typeof v === "object" ? JSON.stringify(v) : String(v).substring(0,50)}</span></div>
                        ))}
                      </div>
                    ) : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
